import type { HtmlFromDotcms } from './converters-from-dotcms';
import {
  convertDotcmsResponseToRecord,
  wrapDotcmsHtml,
} from './converters-from-dotcms';
import type { IBaseObjectData } from './dotcms-base-object';
import type { IGqlHelper } from './gql-helper';

interface IBlockEditorBaseData {
  type: string;
}

interface IBlockEditorContentData extends IBlockEditorBaseData {
  attrs?: object;
  content?: IBlockEditorContentData[] | IBlockEditorTextData[];
}

interface IBlockEditorTextData extends IBlockEditorBaseData {
  marks?: IBlockEditorBaseData[];
  text: string;
}

interface IBlockEditorGqlHelper extends IGqlHelper<IBlockEditorData> {
  processGqlDataAsArrayOfHtmls: (
    gqlResponse: unknown,
  ) => HtmlFromDotcms[] | undefined;

  processGqlDataAsArrayOfStrings: (
    gqlResponse: unknown,
  ) => string[] | undefined;
}

export type IBlockEditorData = IBaseObjectData &
IBlockEditorBaseData & {
  attributes?: object;
  content?: IBlockEditorData[] | string;
  html: string;
  text: string;
};

export const BlockEditorGqlHelper: IBlockEditorGqlHelper = {
  contentTypeName: null,
  gqlFieldsQuery: `
    json
  `,
  processGqlData: (gqlResponse: unknown) => {
    if (!gqlResponse) {
      return undefined;
    }

    const { json } = convertDotcmsResponseToRecord(gqlResponse);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    if (!json || Object.keys(json).length === 0) {
      return undefined;
    }

    const getContentAsText = (innerContent: string[]) => {
      const content = innerContent.join('');
      return content;
    };

    const getContentAsHtml = (type: string, innerContent: string[]) => {
      const content = getContentAsText(innerContent);
      switch (type) {
        case 'bold':
          return `<strong>${content}</strong>`;
        case 'italic':
          return `<em>${content}</em>`;
        case 'strike':
          return `<span style="text-decoration: line-through;">${content}</span>`;
        case 'underline':
          return `<span style="text-decoration: underline;">${content}</span>`;
        case 'paragraph':
          return `<p>${content}</p>`;
        default:
          return content;
      }
    };

    const convertBlockEditorData = (
      data: IBlockEditorContentData | IBlockEditorTextData,
    ): IBlockEditorData => {
      if (data.type === 'text') {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const dataAsText = data as IBlockEditorTextData;
        let contentHtml = dataAsText.text;
        let contentText = dataAsText.text;
        dataAsText.marks?.forEach((x) => {
          contentHtml = getContentAsHtml(x.type, [contentHtml]);
          contentText = getContentAsText([contentText]);
        });
        return {
          type: data.type,
          attributes: dataAsText.marks,
          content: dataAsText.text,
          html: contentHtml,
          text: contentText,
        };
      }

      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const dataAsContent = data as IBlockEditorContentData;
      const content = dataAsContent.content
        ? dataAsContent.content.map(convertBlockEditorData)
        : undefined;
      return {
        type: data.type,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        attributes: dataAsContent.attrs,
        content,
        html: content
          ? getContentAsHtml(
            data.type,
            content.map((x) => x.html),
          )
          : '',
        text: content ? getContentAsText(content.map((x) => x.text)) : '',
      };
    };

    const convertedData = convertBlockEditorData(
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      json as IBlockEditorContentData,
    );
    return convertedData;
  },

  processGqlDataAsArrayOfHtmls: (
    gqlResponse: unknown,
  ): HtmlFromDotcms[] | undefined => {
    const blocks = BlockEditorGqlHelper.processGqlData(gqlResponse);
    let htmls: HtmlFromDotcms[] | undefined;
    if (blocks) {
      const rawContent =
        blocks.content === typeof 'string'
          ? [blocks.content]
          : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          (blocks.content as IBlockEditorData[]).map((x) => x.html);
      htmls = rawContent
        .filter((x) => x && x !== '')
        .map((x) => wrapDotcmsHtml(x));
    }

    return htmls;
  },

  processGqlDataAsArrayOfStrings: (
    gqlResponse: unknown,
  ): string[] | undefined => {
    const blocks = BlockEditorGqlHelper.processGqlData(gqlResponse);
    let strings: string[] | undefined;
    if (blocks) {
      const rawContent =
        blocks.content === typeof 'string'
          ? [blocks.content]
          : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          (blocks.content as IBlockEditorData[]).map((x) => x.text);
      strings = rawContent.filter((x) => x && x !== '');
    }

    return strings;
  },
};
